// video_player.jsx — verified exercise demo videos.
//
// Data: src/exercise_videos.json, one curated YouTube demo per exercise id
// (built-in x_*, catalog fx_*, generator xg_*), every id checked against
// YouTube's oEmbed endpoint by tools/verify-videos.mjs (title + channel recorded).
//
//   demoFor({ id, name, link })  → demo | null
//       A link the user pasted themselves wins (demo.own = true); otherwise the
//       curated video for the id, its built-in/catalog twin, or an exact name match.
//   DemoPlayer   16:9 player: thumbnail first, tap to play (youtube-nocookie,
//                privacy-enhanced), honours start/end. Offline it shows the
//                catalog photo pair and "Video needs a connection".
//                loop: muted autoplay of the demo segment (exercise detail hero).
//   DemoCredit   "Title · Channel" line with an Open-in-YouTube link.
//   FormSheet    in-workout form sheet: video (or the photo pair), key cues,
//                and an honestly labelled YouTube search when there's no video.
//   PhotoPair    the start/end catalog photos, cross-fading.
//   youtubeSearchUrl(name)
//
// Security: embed and link URLs are built only from the 11-char id and integer
// seconds — never from raw user input (parseYouTube is the boundary for links).

import React from 'react';
import VIDEOS from './exercise_videos.json';
import { BUILTIN_MEDIA, catalogEntryFor, exerciseImageUrl, useCatalog } from './exercise_catalog.js';
import { LLSheet } from './sheets.jsx';
import { LL, LLIcon, LLSwitchTrack } from './tokens.jsx';
import { parseYouTube } from './utils.jsx';

const ID_RE = /^[\w-]{11}$/;
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const TABLE = VIDEOS.videos || {};
// Catalog twin → built-in id (fx_Barbell_Bench_Press_-_Medium_Grip → x_bench).
const FROM_CATALOG = Object.fromEntries(Object.entries(BUILTIN_MEDIA).map(([x, fx]) => [fx, x]));
const BY_NAME = new Map();
for (const [key, v] of Object.entries(TABLE)) {
  for (const n of [v.name, ...(v.aka || [])]) if (n && !BY_NAME.has(norm(n))) BY_NAME.set(norm(n), key);
}
// The demo links the app seeds on a few built-ins. They're not the user's own
// choice, so the curated (credited) video takes precedence over them.
const STOCK_LINKS = new Set(['8urE8Z8AMQ4', 'YQ2s_Y7g5Qk', 'rT7DgCr-3pg', 'eGo4IYlbE5g', 'op9kVnSso6Q', 'SW_C1A-rejs']);

function curated(id, name) {
  const key = (id && (TABLE[id] ? id : TABLE[BUILTIN_MEDIA[id]] ? BUILTIN_MEDIA[id] : TABLE[FROM_CATALOG[id]] ? FROM_CATALOG[id] : null))
    || BY_NAME.get(norm(name)) || null;
  if (!key) return null;
  const v = TABLE[key];
  if (!v || !ID_RE.test(v.yt || '')) return null;
  return { key, yt: v.yt, start: v.start | 0, end: v.end | 0, secs: v.secs | 0, title: v.title, channel: v.channel, own: false };
}

export function demoFor({ id, name, link } = {}) {
  const mine = parseYouTube(link || '');
  const cur = curated(id, name);
  if (mine && ID_RE.test(mine.id) && !STOCK_LINKS.has(mine.id)) {
    if (cur && cur.yt === mine.id) return { ...cur, start: mine.start || cur.start };
    return { key: null, yt: mine.id, start: mine.start | 0, end: 0, secs: 0, title: '', channel: '', own: true };
  }
  if (cur) return cur;
  if (mine && ID_RE.test(mine.id)) return { key: null, yt: mine.id, start: mine.start | 0, end: 0, secs: 0, title: '', channel: '', own: true };
  return null;
}

export const videoCount = () => Object.keys(TABLE).length;
export const youtubeSearchUrl = (name) => `https://www.youtube.com/results?search_query=${encodeURIComponent(`${name || ''} exercise form`.trim())}`;
export const youtubeWatchUrl = (d) => `https://www.youtube.com/watch?v=${d.yt}${d.start ? `&t=${d.start}s` : ''}`;

const clock = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
function demoLength(d) {
  if (d.end && d.end > d.start) return d.end - d.start;
  if (d.secs) return Math.max(0, d.secs - d.start);
  return 0;
}

function reducedMotion() { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } }
function saveData() { try { return !!navigator.connection?.saveData; } catch { return false; } }

export function useOnline() {
  const [on, setOn] = React.useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  React.useEffect(() => {
    const up = () => setOn(true), dn = () => setOn(false);
    window.addEventListener('online', up); window.addEventListener('offline', dn);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', dn); };
  }, []);
  return on;
}

// ——— Photo pair (fallback) ——————————————————————————————————————————————————

export function PhotoPair({ img, name, style = {}, children }) {
  const frames = (img || []).filter(Boolean).slice(0, 2).map(exerciseImageUrl);
  const [frame, setFrame] = React.useState(0);
  const [ok, setOk] = React.useState([false, false]);
  React.useEffect(() => {
    if (frames.length < 2 || !ok[0] || !ok[1] || reducedMotion()) return undefined;
    const t = setInterval(() => setFrame(f => 1 - f), 1600);
    return () => clearInterval(t);
  }, [frames.length, ok]);
  return (
    <div style={{ position: 'relative', aspectRatio: '16 / 9', borderRadius: LL.r.md, overflow: 'hidden', background: LL.surface2, ...style }}>
      {frames.map((src, i) => (
        <img key={src} src={src} alt={i === 0 ? `${name} start position` : `${name} end position`} draggable={false} loading="lazy"
          onLoad={() => setOk(o => { const n = o.slice(); n[i] = true; return n; })}
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain',
            filter: 'grayscale(.4) brightness(.92)', opacity: frame === i && ok[i] ? 1 : 0, transition: 'opacity .6s cubic-bezier(.4,0,.2,1)',
          }} />
      ))}
      {!frames.length && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: LL.fgFaint }}>
          <LLIcon.dumbbell style={{ width: 28, height: 28 }} />
        </div>
      )}
      {children}
    </div>
  );
}

// ——— Player ————————————————————————————————————————————————————————————————

const chip = {
  height: 26, padding: '0 10px', borderRadius: LL.r.pill, display: 'inline-flex', alignItems: 'center', gap: 6,
  background: 'rgba(0,0,0,.62)', color: '#fff', fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600,
  backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
};

let _frameSeq = 0;
function embedUrl(d, { loop = false } = {}) {
  const p = new URLSearchParams({ playsinline: '1', rel: '0', modestbranding: '1', autoplay: '1', enablejsapi: '1' });
  if (d.start) p.set('start', String(d.start));
  if (d.end) p.set('end', String(d.end));
  if (loop) { p.set('mute', '1'); p.set('controls', '0'); p.set('disablekb', '1'); p.set('iv_load_policy', '3'); }
  try { p.set('origin', window.location.origin); } catch { /* noop */ }
  return `https://www.youtube-nocookie.com/embed/${d.yt}?${p.toString()}`;
}

// Loops the start..end segment: the embed's own loop=1 restarts at 0:00, so we
// listen to the player's state over postMessage and seek back on "ended".
function useSegmentLoop(ref, d, active) {
  React.useEffect(() => {
    if (!active) return undefined;
    const frame = ref.current;
    if (!frame) return undefined;
    const id = ++_frameSeq;
    const send = (msg) => { try { frame.contentWindow?.postMessage(JSON.stringify(msg), '*'); } catch { /* noop */ } };
    const hello = () => send({ event: 'listening', id, channel: 'widget' });
    const onMsg = (e) => {
      if (e.source !== frame.contentWindow || typeof e.data !== 'string') return;
      let m; try { m = JSON.parse(e.data); } catch { return; }
      const st = m.event === 'onStateChange' ? m.info : m.event === 'infoDelivery' ? m.info?.playerState : undefined;
      if (st === 0) {
        send({ event: 'command', func: 'seekTo', args: [d.start || 0, true] });
        send({ event: 'command', func: 'playVideo', args: [] });
      }
    };
    window.addEventListener('message', onMsg);
    frame.addEventListener('load', hello);
    const t = setTimeout(hello, 1200);
    return () => { window.removeEventListener('message', onMsg); frame.removeEventListener('load', hello); clearTimeout(t); };
  }, [ref, d.yt, d.start, active]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function DemoPlayer({ demo, name, img, loop = false, paused = false, radius = LL.r.md, style = {} }) {
  const online = useOnline();
  const [playing, setPlaying] = React.useState(false);
  const [thumbBad, setThumbBad] = React.useState(false);
  const frameRef = React.useRef(null);
  React.useEffect(() => { setPlaying(false); setThumbBad(false); }, [demo?.yt]);
  React.useEffect(() => { if (paused) setPlaying(false); }, [paused]);
  const looping = loop && !paused && !playing && online && !thumbBad;
  useSegmentLoop(frameRef, demo || {}, looping);
  if (!demo) return <PhotoPair img={img} name={name} style={{ borderRadius: radius, ...style }} />;

  const box = { position: 'relative', aspectRatio: '16 / 9', borderRadius: radius, overflow: 'hidden', background: '#000', ...style };
  if (!online || thumbBad) {
    return (
      <PhotoPair img={img} name={name} style={{ borderRadius: radius, ...style }}>
        <div style={{ position: 'absolute', left: 10, bottom: 10, ...chip }} role="status">
          <LLIcon.video style={{ width: 15, height: 15 }} /> Video needs a connection
        </div>
      </PhotoPair>
    );
  }
  if ((playing && !paused) || looping) {
    return (
      <div style={box}>
        <iframe
          key={looping ? 'loop' : 'play'}
          ref={frameRef}
          title={`${name || 'Exercise'} demo video`}
          src={embedUrl(demo, { loop: looping })}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', display: 'block', pointerEvents: looping ? 'none' : 'auto' }}
        />
        {looping && (
          <button type="button" onClick={() => setPlaying(true)} aria-label={`Play ${name} demo with sound`}
            style={{ position: 'absolute', inset: 0, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
            <span style={{ position: 'absolute', right: 10, bottom: 10, ...chip }}>
              <LLIcon.play style={{ width: 13, height: 13 }} /> Sound
            </span>
          </button>
        )}
      </div>
    );
  }
  const len = demoLength(demo);
  return (
    <button type="button" onClick={() => setPlaying(true)} aria-label={`Play ${name} demo video${len ? `, ${clock(len)}` : ''}`}
      style={{ ...box, display: 'block', width: '100%', padding: 0, border: 'none', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
      <img src={`https://i.ytimg.com/vi/${demo.yt}/hqdefault.jpg`} alt="" draggable={false} loading="lazy"
        onError={() => setThumbBad(true)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      <span aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,.45))' }} />
      <span aria-hidden="true" style={{
        position: 'absolute', left: '50%', top: '50%', width: 60, height: 60, marginLeft: -30, marginTop: -30, borderRadius: LL.r.pill,
        background: 'rgba(0,0,0,.6)', border: '1.5px solid rgba(255,255,255,.85)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }}><LLIcon.play style={{ width: 22, height: 22, marginLeft: 3 }} /></span>
      {len > 0 && <span aria-hidden="true" style={{ position: 'absolute', right: 10, bottom: 10, ...chip, fontFamily: LL.display, fontSize: LL.fs.meta, fontVariantNumeric: 'tabular-nums' }}>{clock(len)}</span>}
    </button>
  );
}

export function DemoCredit({ demo, style = {} }) {
  if (!demo) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, ...style }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, color: LL.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {demo.own ? 'Your demo video' : demo.title}
        </div>
        <div style={{ fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {demo.own ? 'Linked by you · YouTube' : `${demo.channel} · YouTube`}
        </div>
      </div>
      <a href={youtubeWatchUrl(demo)} target="_blank" rel="noopener noreferrer" aria-label="Open on YouTube"
        style={{ width: 40, height: 40, flexShrink: 0, borderRadius: LL.r.pill, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: LL.fgMute, border: `1px solid ${LL.line}`, textDecoration: 'none' }}>
        <LLIcon.upRight style={{ width: 16, height: 16 }} />
      </a>
    </div>
  );
}

// "Search YouTube" — the honest fallback: an unvetted search, opened outside the app.
export function SearchYouTubeButton({ name, style = {} }) {
  return (
    <a href={youtubeSearchUrl(name)} target="_blank" rel="noopener noreferrer" style={{
      minHeight: 48, borderRadius: LL.r.md, border: `1px solid ${LL.line}`, background: 'transparent', color: LL.fg,
      display: 'flex', alignItems: 'center', gap: 12, padding: '8px 14px', textDecoration: 'none', boxSizing: 'border-box', ...style,
    }}>
      <LLIcon.search style={{ width: 18, height: 18, color: LL.fgMute, flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600 }}>Search YouTube</span>
        <span style={{ display: 'block', fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 1 }}>Opens YouTube. Results aren't checked by Lift Log.</span>
      </span>
      <LLIcon.upRight style={{ width: 16, height: 16, color: LL.fgDim, flexShrink: 0 }} />
    </a>
  );
}

// Exercise-detail hero: the demo, its credit, and the opt-in silent loop.
export function DemoHero({ demo, name, img, loopPref, onLoopPref }) {
  const canLoop = !reducedMotion() && !saveData();
  return (
    <div style={{ background: LL.surface, border: `1px solid ${LL.line}`, borderRadius: LL.r.lg, padding: 8 }}>
      <DemoPlayer demo={demo} name={name} img={img} loop={!!loopPref && canLoop} radius={LL.r.md} />
      <DemoCredit demo={demo} style={{ padding: '10px 6px 4px' }} />
      {onLoopPref && (
        <button type="button" role="switch" aria-checked={!!loopPref} onClick={() => onLoopPref(!loopPref)} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '8px 6px 4px', marginTop: 4,
          background: 'transparent', border: 'none', borderTop: `1px solid ${LL.line}`, cursor: 'pointer', textAlign: 'left',
        }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, color: LL.fg }}>Loop demos silently</span>
            <span style={{ display: 'block', fontFamily: LL.ui, fontSize: LL.fs.sm, color: LL.fgMute, marginTop: 1 }}>
              {canLoop ? 'Plays muted on exercise pages. Uses mobile data.' : 'Off while Reduce motion or Data saver is on.'}
            </span>
          </span>
          <LLSwitchTrack on={!!loopPref} />
        </button>
      )}
    </div>
  );
}

// ——— In-workout form sheet ————————————————————————————————————————————————————

export function FormSheet({ open, onClose, exerciseId, name, link }) {
  const catalog = useCatalog();
  // Keep the last exercise on screen while the sheet slides away; the player
  // itself stops (unmounts its iframe) the moment the sheet closes.
  const [snap, setSnap] = React.useState(null);
  const [opens, setOpens] = React.useState(0);
  React.useEffect(() => {
    if (open) { setSnap({ exerciseId, name, link }); setOpens(n => n + 1); }
  }, [open, exerciseId, name, link]);
  const cur = open ? { exerciseId, name, link } : snap;
  const entry = cur ? catalogEntryFor(cur.exerciseId, catalog) : null;
  const demo = cur ? demoFor({ id: cur.exerciseId, name: cur.name, link: cur.link }) : null;
  const cues = (entry?.steps || []).slice(0, 3);
  return (
    <LLSheet open={open} onClose={onClose} title={cur?.name || ''} subtitle={demo ? 'Form video' : 'Form guide'} maxHeight="90%">
      {cur && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <DemoPlayer key={`${demo?.yt || 'photos'}-${opens}`} demo={demo} name={cur.name} img={entry?.img} paused={!open} />
          {demo ? <DemoCredit demo={demo} /> : (
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fgMute, lineHeight: 1.45 }}>
              No checked demo video for this exercise yet. The photos show the start and end positions.
            </div>
          )}
          {cues.length > 0 && (
            <div style={{ background: LL.surface2, borderRadius: LL.r.md, padding: '12px 14px' }}>
              <div style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: LL.fgDim, marginBottom: 8 }}>Key steps</div>
              <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {cues.map((s, i) => (
                  <li key={i} style={{ display: 'flex', gap: 10, fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fg, lineHeight: 1.45 }}>
                    <span style={{ fontFamily: LL.display, fontWeight: 700, color: LL.fgDim, width: 12, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
                    <span style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {!demo && <SearchYouTubeButton name={cur.name} />}
        </div>
      )}
    </LLSheet>
  );
}
